import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, Modal, Tooltip } from '@playerbase/ui';
import type { FeedbackFormFieldsInput, RatingDisplay } from '@playerbase/types';

import { useShell } from '../../shell/use-shell.js';
import { useDiscordGuildChannels } from '../../games/use-discord-guild-channels.js';
import { useDiscordGuildRoles } from '../../games/use-discord-guild-roles.js';
import { fetchDiscordConnectionForGame } from '../../games/discord-data.js';
import {
  fetchFeedbackForm,
  fetchFeedbackFormFields,
  replaceFeedbackFormFields,
  requestFormPermissionCheck,
  updateFeedbackForm,
  type FeedbackFormRpcError,
} from '../../games/feedback-forms-data.js';
import type { FeedbackForm } from '@playerbase/types';
import {
  builderStateFromServer,
  useFormBuilderState,
  type BuilderField,
  type BuilderMetadata,
} from '../../games/use-form-builder-state.js';
import { FieldsCard } from './builder/fields-card.js';
import { MetadataCard } from './builder/metadata-card.js';
import { PreviewModal } from './builder/preview-modal.js';
import { RecruitCard } from './builder/recruit-card.js';
import { RoutingCard } from './builder/routing-card.js';
import { SubmitResponseCard } from './builder/submit-response-card.js';
import { permsAreEnableReady } from './builder/channel-access-panel.js';
import { EnableConfirmModal } from './builder/enable-confirm-modal.js';
import { DisableConfirmModal } from './builder/disable-confirm-modal.js';

interface SyncSnapshot {
  metadata: BuilderMetadata;
  fields: BuilderField[];
}

// `serverId` flips from null to a uuid the first time replace_feedback_form_fields
// returns; it's a persistence detail, not user-visible state. If we compare
// it during dirty / reload checks the form looks "dirty" forever after a
// successful save. Strip it out of every comparison so the surface only
// reflects content the studio actually edits.
type ComparableField = Omit<BuilderField, 'serverId'>;

function fieldShape(f: BuilderField): ComparableField {
  return {
    clientId: f.clientId,
    kind: f.kind,
    title: f.title,
    description: f.description,
    required: f.required,
    config: f.config,
  };
}

function fieldsEqualForCompare(a: BuilderField[], b: BuilderField[]): boolean {
  if (a.length !== b.length) return false;
  return JSON.stringify(a.map(fieldShape)) === JSON.stringify(b.map(fieldShape));
}

const ERROR_COPY: Partial<Record<FeedbackFormRpcError, string>> = {
  not_a_member: "You don't have permission to edit this form.",
  form_enabled_cannot_edit: 'Disable the form before editing it.',
  name_required: 'A form name is required.',
  name_too_long: 'Form name must be 80 characters or fewer.',
  title_required: 'A Discord title is required.',
  title_too_long: 'Discord title must be 45 characters or fewer (Discord modal limit).',
  field_title_required: 'Every field needs a question.',
  field_title_too_long: 'A field question is over the 45 character limit.',
  field_description_too_long: 'A field helper text is over the 100 character limit.',
  rating_display_must_be_object: 'Rating display config is invalid.',
  rating_display_mode_invalid: 'Rating display mode must be numbers or emoji.',
  rating_display_emoji_invalid: 'Pick an emoji (1-8 chars) for emoji-mode ratings.',
  recruit_requires_connection:
    'Connect this game to Discord before configuring recruit channels or roles.',
  recruit_unknown_channel_id:
    "One of the recruit channels is no longer visible to the bot. Remove it and try again.",
  recruit_unknown_role_id:
    'One of the recruit roles is no longer assignable. Remove it and try again.',
  binary_label_must_be_string: 'Binary labels must be text or emoji.',
  binary_label_too_long: 'Binary labels are limited to 32 characters.',
  select_options_required: 'Select fields need at least one option.',
  select_too_many_options: 'Select fields are limited to 25 options.',
  too_many_fields: 'Forms are limited to 5 fields (Discord modal cap).',
  only_one_nps_field_per_form: 'Only one NPS field per form.',
  at_least_one_field_required: 'Add at least one field before saving.',
};

// ---------------------------------------------------------------------------
// Form builder page.
//
// Lives at /g/:gameShortId/feedback-settings/forms/:formId. Edits stay local
// until the studio clicks Save; explicit saves keep the model simple and
// avoid the keystroke-vs-realtime races that auto-save invites. We do
// guard against accidental loss with a beforeunload warning + an in-app
// nav blocker when the local state has unsaved changes.
//
// Realtime updates flow through ShellProvider's `feedbackFormsVersion`
// counter; a remote change re-seeds the local state only when there are
// no pending local edits to clobber. The page does NOT spawn its own
// subscription (one channel per app rule).
// ---------------------------------------------------------------------------
export function FormBuilderPage(): JSX.Element {
  const params = useParams<{ formId: string; studioShortId: string; gameShortId: string }>();
  const { formId, studioShortId, gameShortId } = params;
  const { activeGame, feedbackFormsVersion } = useShell();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [guildId, setGuildId] = useState<string | null>(null);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [enableConfirmOpen, setEnableConfirmOpen] = useState(false);
  const [disableConfirmOpen, setDisableConfirmOpen] = useState(false);
  // The synced form drives the channel-access panel + Enable readiness;
  // it tracks the most recent server snapshot so Realtime perm-check ticks
  // flow through without re-rendering the builder reducer.
  const [form, setForm] = useState<FeedbackForm | null>(null);

  const builder = useFormBuilderState({
    metadata: {
      name: '',
      internalDescription: '',
      title: '',
      discordDescription: '',
      surveyChannelId: null,
      responseChannelId: null,
      repostFrequency: 'never',
      anonymous: false,
      submitResponseTitle: '',
      submitResponseDescription: '',
      recruitChannelIds: [],
      recruitRoleIds: [],
    },
    fields: [],
    lastError: null,
  });

  const { channels, loading: channelsLoading } = useDiscordGuildChannels(connectionId);
  const { roles, loading: rolesLoading } = useDiscordGuildRoles(connectionId);

  // Last-saved snapshot. Used to compute "dirty" (the Save button enables
  // and unload warnings fire) and to skip Realtime echoes after our own
  // writes. Null before the initial fetch lands.
  const syncedRef = useRef<SyncSnapshot | null>(null);

  // Mirror of builder.state so reload() can read the latest local edits
  // without depending on builder (which is a fresh object every render).
  const stateRef = useRef(builder.state);
  stateRef.current = builder.state;
  const builderRef = useRef(builder);
  builderRef.current = builder;

  // ---- Loading + Realtime reload -----------------------------------------

  const reload = useCallback(async (): Promise<void> => {
    if (formId === undefined) return;
    const [loadedForm, fields] = await Promise.all([
      fetchFeedbackForm(formId),
      fetchFeedbackFormFields(formId),
    ]);
    if (loadedForm === null) {
      setLoadError('Form not found.');
      setLoading(false);
      return;
    }
    setForm(loadedForm);
    const seed = builderStateFromServer(loadedForm, fields);
    const seedSnapshot: SyncSnapshot = { metadata: seed.metadata, fields: seed.fields };

    if (syncedRef.current === null) {
      builderRef.current.reset(seed);
      syncedRef.current = seedSnapshot;
      setLoading(false);
      return;
    }

    // Subsequent loads (Realtime ticks). Don't clobber local edits; don't
    // re-seed when the server is just echoing back our own write.
    const localDirty =
      JSON.stringify(stateRef.current.metadata) !==
        JSON.stringify(syncedRef.current.metadata) ||
      !fieldsEqualForCompare(stateRef.current.fields, syncedRef.current.fields);
    if (localDirty) return;

    const serverEchoesSynced =
      JSON.stringify(seedSnapshot.metadata) ===
        JSON.stringify(syncedRef.current.metadata) &&
      fieldsEqualForCompare(seedSnapshot.fields, syncedRef.current.fields);
    if (serverEchoesSynced) return;

    builderRef.current.reset(seed);
    syncedRef.current = seedSnapshot;
  }, [formId]);

  useEffect(() => {
    void reload();
  }, [reload, feedbackFormsVersion]);

  // Auto-recheck perms when landing on a disabled form whose last check
  // has aged past the 5-minute Enable window. Without this, simple
  // disable → re-enable toggles get stuck (Enable stays gated on a
  // fresh check, and the only check trigger today is Save). Keyed to
  // formId + disabledAt so each fresh disable cycle gets one new check
  // instead of being blocked by an earlier per-mount one-shot; the
  // RPC's 30s server floor catches the rest.
  const autoRecheckedRef = useRef<string | null>(null);
  useEffect(() => {
    if (formId === undefined || form === null) return;
    if (form.status !== 'disabled') return;
    if (form.surveyChannelId === null || form.responseChannelId === null) return;
    if (permsAreEnableReady(form)) return;
    const cycleKey = `${formId}:${form.disabledAt ?? 'never'}`;
    if (autoRecheckedRef.current === cycleKey) return;
    autoRecheckedRef.current = cycleKey;
    void requestFormPermissionCheck(formId);
  }, [formId, form]);

  // Resolve the Discord connection id once per active game so the channel
  // pickers can hydrate.
  useEffect(() => {
    if (activeGame === null) {
      setConnectionId(null);
      setGuildId(null);
      return;
    }
    let cancelled = false;
    void fetchDiscordConnectionForGame(activeGame.id).then((conn) => {
      if (cancelled) return;
      setConnectionId(conn?.id ?? null);
      setGuildId(conn?.guildId ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [activeGame]);

  // ---- Dirty detection + unload guards ------------------------------------

  const dirty = (() => {
    const synced = syncedRef.current;
    if (synced === null) return false;
    return (
      JSON.stringify(synced.metadata) !== JSON.stringify(builder.state.metadata) ||
      !fieldsEqualForCompare(synced.fields, builder.state.fields)
    );
  })();

  // Browser-level guard: tab close / refresh / cross-origin nav.
  useEffect(() => {
    if (!dirty) return undefined;
    function onBeforeUnload(e: BeforeUnloadEvent): void {
      e.preventDefault();
      // Modern browsers ignore the message string and show their own copy,
      // but assigning returnValue is still required to trigger the prompt.
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  // Intra-app nav guard is intentionally limited to the explicit "All
  // forms" back button (handled below via window.confirm). React Router's
  // useBlocker only works under a data router and the portal uses the
  // legacy <Routes> API; reaching for it here would mean a router rewrite
  // out of scope for this PR. Sidebar nav while dirty therefore does NOT
  // prompt - same trade-off the rest of the portal makes today. Tab
  // close / refresh / cross-origin nav are still caught by beforeunload.

  // ---- Save plumbing ------------------------------------------------------

  const pushMetadata = useCallback(
    async (
      metadata: BuilderMetadata,
    ): Promise<{ error: FeedbackFormRpcError | null; form: FeedbackForm | null }> => {
      if (formId === undefined) return { error: 'unknown', form: null };
      const result = await updateFeedbackForm(formId, {
        name: metadata.name === '' ? 'Untitled form' : metadata.name,
        internalDescription:
          metadata.internalDescription === '' ? null : metadata.internalDescription,
        title:
          metadata.title === ''
            ? // Discord title falls back to the first 45 chars of the
              // portal name so a freshly-created form still has a
              // sensible modal title before the studio refines it.
              (metadata.name === '' ? 'Untitled form' : metadata.name).slice(0, 45)
            : metadata.title,
        discordDescription:
          metadata.discordDescription === '' ? null : metadata.discordDescription,
        surveyChannelId: metadata.surveyChannelId,
        responseChannelId: metadata.responseChannelId,
        repostFrequency: metadata.repostFrequency,
        anonymous: metadata.anonymous,
        submitResponseTitle:
          metadata.submitResponseTitle === '' ? null : metadata.submitResponseTitle,
        submitResponseDescription:
          metadata.submitResponseDescription === '' ? null : metadata.submitResponseDescription,
        recruitChannelIds: metadata.recruitChannelIds,
        recruitRoleIds: metadata.recruitRoleIds,
      });
      return result.ok
        ? { error: null, form: result.data }
        : { error: result.error, form: null };
    },
    [formId],
  );

  const pushFields = useCallback(
    async (fields: BuilderField[]): Promise<FeedbackFormRpcError | null> => {
      if (formId === undefined) return 'unknown';
      // The DB requires at least one field. An empty list means "save the
      // metadata only"; we don't call the fields RPC in that case so the
      // user can shape the form skeleton before adding fields.
      if (fields.length === 0) return null;
      const wire = fields.map<FeedbackFormFieldsInput[number]>((f) => {
        const base = {
          title: f.title === '' ? 'Untitled' : f.title,
          description: f.description ?? null,
          required: f.required,
        } as const;
        if (f.kind === 'rating') {
          const config = f.config as { display?: RatingDisplay };
          if (config.display?.mode === 'emoji') {
            return {
              kind: 'rating',
              ...base,
              display: { mode: 'emoji', emoji: config.display.emoji },
            };
          }
          return { kind: 'rating', ...base };
        }
        if (f.kind === 'binary') {
          const config = f.config as { trueLabel?: string; falseLabel?: string };
          // Only ship the override keys when the studio set them; the
          // server defaults to Yes / No when absent.
          const extras: { trueLabel?: string; falseLabel?: string } = {};
          if (config.trueLabel !== undefined && config.trueLabel.length > 0) {
            extras.trueLabel = config.trueLabel;
          }
          if (config.falseLabel !== undefined && config.falseLabel.length > 0) {
            extras.falseLabel = config.falseLabel;
          }
          return { kind: 'binary', ...base, ...extras };
        }
        if (f.kind === 'select') {
          const config = f.config as { options: string[]; maxValues?: number };
          // Strip blanks; the RPC rejects empty strings. Fall back to a
          // single placeholder if every option is empty so the save still
          // round-trips (the validation surface tells the studio).
          const options = config.options
            .map((o) => o.trim())
            .filter((o) => o.length > 0);
          const finalOptions = options.length > 0 ? options : ['Option 1'];
          // Clamp maxValues a final time before shipping; reducer already
          // does this, but a stale value could survive an option deletion
          // race.
          const ceiling = Math.min(finalOptions.length, 25);
          const clampedMax =
            config.maxValues !== undefined && config.maxValues > 1
              ? Math.min(config.maxValues, ceiling)
              : undefined;
          if (clampedMax !== undefined && clampedMax > 1) {
            return {
              kind: 'select',
              ...base,
              options: finalOptions,
              maxValues: clampedMax,
            };
          }
          return {
            kind: 'select',
            ...base,
            options: finalOptions,
          };
        }
        return { kind: f.kind, ...base };
      });
      const result = await replaceFeedbackFormFields(formId, wire);
      if (!result.ok) return result.error;
      // Reconcile clientId -> serverId so subsequent saves hit the right rows.
      if (result.data !== null) {
        const ids = result.data.map((server, idx) => {
          const local = fields[idx];
          if (local === undefined) return null;
          return { clientId: local.clientId, serverId: server.id };
        });
        const filtered = ids.filter((entry): entry is { clientId: string; serverId: string } =>
          entry !== null,
        );
        builderRef.current.applyServerIds(filtered);
      }
      return null;
    },
    [formId],
  );

  async function handleSave(): Promise<void> {
    setSaving(true);
    setSaveError(null);
    const snapshot: SyncSnapshot = {
      metadata: builder.state.metadata,
      fields: builder.state.fields,
    };
    const synced = syncedRef.current;
    const metadataChanged =
      synced === null ||
      JSON.stringify(synced.metadata) !== JSON.stringify(snapshot.metadata);
    const fieldsChanged =
      synced === null || !fieldsEqualForCompare(synced.fields, snapshot.fields);

    let firstError: FeedbackFormRpcError | null = null;
    let updatedForm: FeedbackForm | null = null;
    if (metadataChanged) {
      const result = await pushMetadata(snapshot.metadata);
      firstError = result.error;
      updatedForm = result.form;
    }
    if (firstError === null && fieldsChanged) {
      firstError = await pushFields(snapshot.fields);
    }

    if (firstError !== null) {
      setSaving(false);
      setSaveError(ERROR_COPY[firstError] ?? 'Could not save the form. Please try again.');
      return;
    }
    // Refresh the local form snapshot so the readiness gate (Enable
    // button) reflects the just-saved metadata. Without this, status /
    // updated_at / channel ids drift from what the user just persisted
    // until the next Realtime tick lands.
    if (updatedForm !== null) setForm(updatedForm);

    // Fire a fresh permission check when (a) either channel changed in
    // this save, (b) recruit channels or roles changed (so the recruit
    // pre-flight reflects the new selection), or (c) the form is
    // currently disabled — covers the disable → edit → save →
    // re-enable flow where the previous perm check has aged past the
    // 5-minute window the Enable RPC enforces.
    const surveyChanged =
      synced === null
      || synced.metadata.surveyChannelId !== snapshot.metadata.surveyChannelId;
    const responseChanged =
      synced === null
      || synced.metadata.responseChannelId !== snapshot.metadata.responseChannelId;
    const recruitChanged =
      synced === null
      || JSON.stringify(synced.metadata.recruitChannelIds)
        !== JSON.stringify(snapshot.metadata.recruitChannelIds)
      || JSON.stringify(synced.metadata.recruitRoleIds)
        !== JSON.stringify(snapshot.metadata.recruitRoleIds);
    const targetForm = updatedForm ?? form;
    const needsRefresh =
      surveyChanged
      || responseChanged
      || recruitChanged
      || (targetForm !== null && targetForm.status === 'disabled');
    if (formId !== undefined && needsRefresh) {
      void requestFormPermissionCheck(formId);
    }
    syncedRef.current = snapshot;
    setSaving(false);
  }

  // ---- Render -------------------------------------------------------------

  if (loading) {
    return <div className="px-2 py-6 text-sm text-text-muted">Loading form...</div>;
  }
  if (loadError !== null) {
    return (
      <div className="px-2 py-6">
        <Alert tone="danger">{loadError}</Alert>
      </div>
    );
  }
  if (formId === undefined || activeGame === null) {
    return <div className="px-2 py-6 text-sm text-text-muted">Loading...</div>;
  }

  // Absolute path - `navigate('../')` resolves up the route tree and
  // lands on the /feedback-settings index, which redirects to
  // Watched channels. We want the forms list explicitly.
  const formsListPath =
    params.studioShortId !== undefined && params.gameShortId !== undefined
      ? `/s/${params.studioShortId}/g/${params.gameShortId}/feedback-settings/forms`
      : '..';

  function handleBack(): void {
    if (dirty) {
      setDiscardConfirmOpen(true);
      return;
    }
    navigate(formsListPath);
  }

  const headingTitle =
    builder.state.metadata.name.trim() === ''
      ? 'Untitled form'
      : builder.state.metadata.name;

  const status = form?.status ?? 'draft';
  const isEnabled = status === 'enabled';
  // Mirror of the server-side readiness rule. If any condition fails the
  // Enable button stays disabled with a tooltip explaining what's missing.
  const enableBlockReason = ((): string | null => {
    if (form === null) return 'Loading form...';
    if (dirty) return 'Save your changes first.';
    if (builder.state.metadata.surveyChannelId === null) return 'Pick a survey channel.';
    if (builder.state.metadata.responseChannelId === null) return 'Pick a response channel.';
    if (builder.state.metadata.submitResponseTitle.trim() === '') {
      return 'Add a thank-you message title.';
    }
    if (builder.state.fields.length === 0) return 'Add at least one field.';
    if (!permsAreEnableReady(form)) return 'Run a fresh permission check.';
    return null;
  })();

  return (
    <div className="flex min-h-full flex-1 flex-col">
      {/* Cards sit in a max-w-5xl column, centered. */}
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-5 px-6 py-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <span className="font-mono text-[10px] font-semibold uppercase tracking-wider text-text-muted">
              Feedback form
            </span>
            <h1 className="font-display text-2xl font-bold text-text">
              {headingTitle}
            </h1>
            <div className="flex items-center gap-2 text-xs text-text-muted">
              <span className="rounded-sm border border-border bg-surface-2 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider">
                {status === 'enabled' ? 'Enabled' : status === 'disabled' ? 'Disabled' : 'Draft'}
              </span>
              <span>
                {isEnabled ? 'Live - disable to edit' : dirty ? 'Unsaved changes' : 'All changes saved'}
              </span>
            </div>
          </div>
          {/* View submissions appears once the form has been enabled at
              least once - drafts have no submissions yet, so the link
              would always lead to an empty state. The destination
              tolerates zero submissions either way. */}
          {form !== null && form.enabledAt !== null && studioShortId !== undefined && gameShortId !== undefined ? (
            <Button asChild variant="ghost" size="sm">
              <Link
                to={`/s/${studioShortId}/g/${gameShortId}/feedback-settings/forms/${formId}/responses`}
              >
                View submissions &rarr;
              </Link>
            </Button>
          ) : null}
        </div>

        {saveError !== null ? <Alert tone="danger">{saveError}</Alert> : null}

        {isEnabled ? (
          <Alert tone="info">This form is live. Disable it before making changes.</Alert>
        ) : null}

        {/* When the form is enabled, lock the editing surface. The server-side
            RPCs reject writes against enabled forms; matching that on the
            client keeps the UX honest and avoids a Save button that does
            nothing. Wrapping in a single read-only container is simpler than
            plumbing a `disabled` prop through every card and its inputs. */}
        <div
          className={isEnabled ? 'pointer-events-none select-none opacity-60' : undefined}
          aria-disabled={isEnabled}
        >
          <div className="flex flex-col gap-5">
            <MetadataCard metadata={builder.state.metadata} builder={builder} />
            <FieldsCard builder={builder} />
            <SubmitResponseCard metadata={builder.state.metadata} builder={builder} />
            <RecruitCard
              metadata={builder.state.metadata}
              builder={builder}
              channels={channels}
              channelsLoading={channelsLoading}
              roles={roles}
              rolesLoading={rolesLoading}
              hasConnection={connectionId !== null}
              form={form}
              guildId={guildId}
            />
            <RoutingCard
              metadata={builder.state.metadata}
              builder={builder}
              channels={channels}
              channelsLoading={channelsLoading}
              hasConnection={connectionId !== null}
              form={form}
              guildId={guildId}
            />
          </div>
        </div>
      </div>

      {/* Sticky footer toolbar. Sits OUTSIDE the max-w-5xl column so its
          background spans the full width of <main>; its inner content
          stays centered to the same column width as the cards. PR 4
          wires Enable. */}
      <div className="sticky bottom-0 z-10 mt-auto border-t border-border bg-surface">
        <div className="mx-auto flex w-full max-w-5xl items-center justify-between gap-3 px-6 py-3">
          <span className="text-xs text-text-muted">
            {isEnabled ? 'Live - read-only' : dirty ? 'Unsaved changes' : 'Up to date'}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" type="button" onClick={handleBack}>
              &larr; Exit
            </Button>
            <Button variant="ghost" type="button" onClick={() => setPreviewOpen(true)}>
              Preview
            </Button>
            {/* Save is hidden when the form is enabled — the surface above is
                read-only, so there is nothing to save. Disable replaces the
                Enable affordance. */}
            {isEnabled ? null : (
              <Button
                variant="primary"
                type="button"
                onClick={() => void handleSave()}
                disabled={saving || !dirty}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            )}
            {isEnabled ? (
              <Button
                variant="danger"
                type="button"
                onClick={() => setDisableConfirmOpen(true)}
              >
                Disable
              </Button>
            ) : (
              <Tooltip content={enableBlockReason}>
                <Button
                  variant="primary"
                  type="button"
                  disabled={enableBlockReason !== null}
                  aria-disabled={enableBlockReason !== null}
                  onClick={() => setEnableConfirmOpen(true)}
                >
                  Enable
                </Button>
              </Tooltip>
            )}
          </div>
        </div>
      </div>

      <PreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        metadata={builder.state.metadata}
        fields={builder.state.fields}
      />

      {form !== null ? (
        <>
          <EnableConfirmModal
            open={enableConfirmOpen}
            form={form}
            onClose={() => setEnableConfirmOpen(false)}
            onEnabled={(updated) => {
              setForm(updated);
              setEnableConfirmOpen(false);
            }}
          />
          <DisableConfirmModal
            open={disableConfirmOpen}
            form={form}
            onClose={() => setDisableConfirmOpen(false)}
            onDisabled={(updated) => {
              setForm(updated);
              setDisableConfirmOpen(false);
              // Kick off a fresh perm check so the Enable button isn't
              // gated on the now-stale prior check when the user wants
              // to toggle back. The RPC's 30s server floor handles
              // accidental thrash.
              if (formId !== undefined) void requestFormPermissionCheck(formId);
            }}
          />
        </>
      ) : null}

      <Modal
        open={discardConfirmOpen}
        onClose={() => setDiscardConfirmOpen(false)}
        labelledById="discard-changes-title"
        size="sm"
      >
        <Card>
          <Card.Header>
            <Card.Title id="discard-changes-title">Leave without saving?</Card.Title>
            <Card.Description>
              You have unsaved changes to this form. They will be lost if you leave now.
            </Card.Description>
          </Card.Header>
          <Card.Footer>
            <Button variant="ghost" onClick={() => setDiscardConfirmOpen(false)}>
              Keep editing
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                setDiscardConfirmOpen(false);
                navigate(formsListPath);
              }}
            >
              Discard changes
            </Button>
          </Card.Footer>
        </Card>
      </Modal>
    </div>
  );
}
